import { useState, useCallback, useEffect, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Table, Badge, Button, Form, Modal, Card, Dropdown, DropdownButton, Spinner, Alert } from 'react-bootstrap'
import Lottie from 'lottie-react'
import loaderIris from '../../../lotties/loader-iris.json'
import { usePriceExtractionsFetch } from '../../../hooks/fetch/usePriceExtractionsFetch'
import {
    PriceExtraction,
    PriceChange,
    AvailableUnitsMap,
    ChangeStatus,
    ExtractionStatus,
    SuggestedAction,
    DiscrepancyRun,
    DiscrepancyType
} from '../../../interfaces/priceExtraction'
import SpecialSelect, { OptionSelectType } from '../../../components/ui/SpecialSelect'

const DIFFERENCE_THRESHOLD = 20 // Porcentaje para resaltar cambios grandes
const CHILE_COUNTRY_ID = 7 // ID de Chile para lógica condicional de precios

const statusLabels: Record<ChangeStatus, string> = {
    pending: 'Pendiente',
    accepted: 'Aceptado',
    rejected: 'Rechazado',
    applied: 'Aplicado',
    skipped: 'Omitido'
}

const statusColors: Record<ChangeStatus, string> = {
    pending: 'secondary',
    accepted: 'success',
    rejected: 'danger',
    applied: 'info',
    skipped: 'warning'
}

const extractionStatusLabels: Record<ExtractionStatus, string> = {
    pending: 'Pendiente',
    processing: 'Procesando',
    completed: 'Validar',
    failed: 'Fallida',
    validated: 'Validada',
    applied: 'Aplicada'
}

const actionLabels: Record<SuggestedAction, string> = {
    update_price: 'Actualizar',
    activate: 'Activar',
    deactivate: 'Desactivar',
    map: 'Mapear'
}

const actionColors: Record<SuggestedAction, string> = {
    update_price: 'primary',
    activate: 'success',
    deactivate: 'danger',
    map: 'warning'
}

// Helper para detectar si una discrepancia fue resuelta
const isResolvedDiscrepancy = (change: PriceChange): boolean => {
    return !change.is_discrepancy &&
           change.discrepancy_runs !== undefined &&
           change.discrepancy_runs !== null &&
           change.discrepancy_runs.length > 0
}

// Helper para obtener el label del tipo de entidad
const getEntityLabel = (change: PriceChange, capitalize = true): string => {
    if (change.entity_type === 'unit') return capitalize ? 'Unidad' : 'unidad'
    if (change.garage_type === 'warehouse') return capitalize ? 'Baulera' : 'baulera'
    return capitalize ? 'Cochera' : 'cochera'
}

export const ValidationView = () => {
    const { id } = useParams<{ id: string }>()
    const {
        getExtractionChanges,
        getExtractionStatus,
        validateChanges,
        applyChanges,
        mapUnit,
        resolveDiscrepancy,
        showAlertMessage
    } = usePriceExtractionsFetch()

    const [extraction, setExtraction] = useState<PriceExtraction | null>(null)
    const [changes, setChanges] = useState<PriceChange[]>([])
    const [availableUnits, setAvailableUnits] = useState<AvailableUnitsMap>({})
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [initialized, setInitialized] = useState(false)
    const [isProcessing, setIsProcessing] = useState(false)
    const pollingRef = useRef<NodeJS.Timeout | null>(null)
    const scrollPositionRef = useRef<number>(0)

    // Filtros
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [projectFilter, setProjectFilter] = useState<number | undefined>()
    const [showOnlyChanges, setShowOnlyChanges] = useState(true)

    // Modal de confirmacion
    const [showApplyModal, setShowApplyModal] = useState(false)

    // Mapeo manual
    const [mappingChange, setMappingChange] = useState<PriceChange | null>(null)
    const [showMappingModal, setShowMappingModal] = useState(false)
    const [selectedMapping, setSelectedMapping] = useState<OptionSelectType | null>(null)

    // Discrepancia modal
    const [discrepancyChange, setDiscrepancyChange] = useState<PriceChange | null>(null)
    const [showDiscrepancyModal, setShowDiscrepancyModal] = useState(false)
    const [selectedRun, setSelectedRun] = useState<number | null>(null)
    const [useMajority, setUseMajority] = useState(false)

    const loadData = useCallback(async (
        statusF?: string,
        projectF?: number,
        onlyChanges?: boolean
    ) => {
        if (!id) return

        scrollPositionRef.current = window.scrollY
        setLoading(true)
        const result = await getExtractionChanges(
            parseInt(id),
            statusF === 'all' ? undefined : statusF,
            projectF,
            onlyChanges
        )
        setLoading(false)

        if (result.success) {
            setExtraction(result.extraction)
            setChanges(result.data)
            setAvailableUnits(result.availableUnits)
            requestAnimationFrame(() => {
                window.scrollTo(0, scrollPositionRef.current)
            })
        } else {
            showAlertMessage('danger', result.message || 'Error al cargar datos')
        }
    }, [id, getExtractionChanges, showAlertMessage])

    // Verificar status para polling
    const checkStatus = useCallback(async () => {
        if (!id) return

        const result = await getExtractionStatus(parseInt(id))
        if (result.success && result.data) {
            // Actualizar estado de procesamiento
            setIsProcessing(result.data.is_processing)

            // Si ya completó o falló, detener polling y cargar datos
            if (result.data.is_completed || result.data.is_failed) {
                if (pollingRef.current) {
                    clearInterval(pollingRef.current)
                    pollingRef.current = null
                }
                // Recargar datos completos
                loadData(statusFilter, projectFilter, showOnlyChanges)
            }
        }
    }, [id, getExtractionStatus, loadData, statusFilter, projectFilter, showOnlyChanges])

    // Inicializar datos una sola vez
    if (!initialized && id) {
        setInitialized(true)
        // Primero verificar el status
        checkStatus().then(() => {
            loadData(statusFilter, projectFilter, showOnlyChanges)
        })
    }

    // Efecto para manejar polling cuando está procesando
    useEffect(() => {
        if (isProcessing && !pollingRef.current) {
            // Iniciar polling cada 3 segundos
            pollingRef.current = setInterval(checkStatus, 3000)
        }

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current)
                pollingRef.current = null
            }
        }
    }, [isProcessing, checkStatus])

    const handleStatusFilter = (status: string) => {
        setStatusFilter(status)
        loadData(status, projectFilter, showOnlyChanges)
    }

    const handleProjectFilter = (projectId: number | undefined) => {
        setProjectFilter(projectId)
        loadData(statusFilter, projectId, showOnlyChanges)
    }

    const handleShowOnlyChanges = (checked: boolean) => {
        setShowOnlyChanges(checked)
        loadData(statusFilter, projectFilter, checked)
    }

    const handleValidateSingle = async (changeId: number, status: ChangeStatus) => {
        setSubmitting(true)
        const result = await validateChanges(parseInt(id!), {
            action: 'individual',
            changes: [{ change_id: changeId, status }]
        })
        setSubmitting(false)

        if (result.success) {
            loadData(statusFilter, projectFilter, showOnlyChanges)
        } else {
            showAlertMessage('danger', result.message)
        }
    }

    const handleValidateAll = async (action: 'accept_all' | 'reject_all') => {
        setSubmitting(true)
        const result = await validateChanges(parseInt(id!), { action })
        setSubmitting(false)

        if (result.success) {
            showAlertMessage('success', `${action === 'accept_all' ? 'Aceptados' : 'Rechazados'} ${result.data?.accepted || result.data?.rejected || 0} cambios`)
            loadData(statusFilter, projectFilter, showOnlyChanges)
        } else {
            showAlertMessage('danger', result.message)
        }
    }

    const handleApplyChanges = async () => {
        setShowApplyModal(false)
        setSubmitting(true)
        const result = await applyChanges(parseInt(id!))
        setSubmitting(false)

        if (result.success) {
            showAlertMessage('success', result.message || `Se aplicaron ${result.data?.applied || 0} cambios a la base de datos`)
            loadData(statusFilter, projectFilter, showOnlyChanges)
        } else {
            showAlertMessage('danger', result.message)
        }
    }

    const openMappingModal = (change: PriceChange) => {
        setMappingChange(change)
        setSelectedMapping(null)
        setShowMappingModal(true)
    }

    const handleMapUnit = async () => {
        if (!mappingChange || !selectedMapping?.value) return

        setSubmitting(true)
        const result = await mapUnit(
            mappingChange.id,
            mappingChange.entity_type === 'unit' ? selectedMapping.value : undefined,
            mappingChange.entity_type === 'garage' ? selectedMapping.value : undefined
        )
        setSubmitting(false)

        if (result.success) {
            showAlertMessage('success', 'Unidad mapeada correctamente')
            setShowMappingModal(false)
            loadData(statusFilter, projectFilter, showOnlyChanges)
        } else {
            showAlertMessage('danger', result.message)
        }
    }

    const formatMoney = (value: number | undefined | null): string => {
        if (value === null || value === undefined) return ''
        return new Intl.NumberFormat('es-ES').format(value)
    }

    const getDifferenceClass = (current: number | undefined | null, extracted: number | undefined | null): string => {
        if (!current || !extracted) return ''
        const diff = Math.abs(((extracted - current) / current) * 100)
        if (diff > DIFFERENCE_THRESHOLD) return 'text-danger fw-bold'
        if (extracted > current) return 'text-danger'
        if (extracted < current) return 'text-success'
        return ''
    }

    const getDifferencePercent = (current: number | undefined | null, extracted: number | undefined | null): string => {
        if (!current || current === 0 || !extracted) return ''
        const diff = ((extracted - current) / current) * 100
        const sign = diff > 0 ? '+' : ''
        return `(${sign}${diff.toFixed(1)}%)`
    }

    // Obtener proyectos unicos para filtro
    const uniqueProjects = Array.from(new Set(changes.map(c => c.project_id)))
        .map(pId => {
            const change = changes.find(c => c.project_id === pId)
            return { id: pId, title: change?.project?.title || `Proyecto ${pId}` }
        })

    // Detectar si es Chile para mostrar columnas condicionales
    const isChile = extraction?.country_id === CHILE_COUNTRY_ID

    // Helper para verificar si tiene cambios reales (usado en contadores)
    const checkHasRealChanges = (c: PriceChange): boolean => {
        const action = c.suggested_action || 'update_price'
        if (action === 'activate' || action === 'deactivate') return true
        return c.has_list_change || c.has_cash_change || c.has_work_change || c.has_discount_change
    }

    // Solo contar pendientes que tienen cambios reales
    const pendingCount = changes.filter(c => c.status === 'pending' && checkHasRealChanges(c)).length
    const acceptedCount = changes.filter(c => c.status === 'accepted').length

    // Contar cambios pendientes que pueden ser aceptados (no requieren mapeo Y tienen cambios reales)
    const acceptablePendingCount = changes.filter(c =>
        c.status === 'pending' &&
        checkHasRealChanges(c) &&
        (c.unit_id || c.garage_id || c.suggested_action !== 'map')
    ).length

    // Contadores por tipo de entidad
    const totalUnits = changes.filter(c => c.entity_type === 'unit').length
    const totalGarages = changes.filter(c => c.entity_type === 'garage' && c.garage_type === 'garage').length
    const totalWarehouses = changes.filter(c => c.entity_type === 'garage' && c.garage_type === 'warehouse').length

    const getMappingOptions = (): OptionSelectType[] => {
        if (!mappingChange) return []
        const projectUnits = availableUnits[mappingChange.project_id]
        if (!projectUnits) return []

        if (mappingChange.entity_type === 'unit') {
            return projectUnits.units.map(u => ({
                value: u.id,
                label: `${u.number} - Lista: ${formatMoney(u.list_price) || 'N/A'} / Contado: ${formatMoney(u.price) || 'N/A'}`
            }))
        } else {
            return projectUnits.garages.map(g => ({
                value: g.id,
                label: `${g.name} - ${formatMoney(g.price) || 'N/A'}`
            }))
        }
    }

    const renderPriceCell = (
        hasChange: boolean,
        currentPrice: number | undefined | null,
        extractedPrice: number | undefined | null,
        suggestedAction?: SuggestedAction
    ) => {
        // Si es desactivar, mostrar el precio actual como que se eliminara
        if (suggestedAction === 'deactivate') {
            return (
                <span className="text-danger">
                    <span className="text-decoration-line-through">{formatMoney(currentPrice) || '-'}</span>
                    <small className="ms-1">(eliminar)</small>
                </span>
            )
        }

        // Si es activar y no hay precio actual, mostrar el extraido
        if (suggestedAction === 'activate' && !currentPrice && extractedPrice) {
            return (
                <span className="text-success">
                    {formatMoney(extractedPrice)}
                    <small className="ms-1">(nuevo)</small>
                </span>
            )
        }

        // Si hay cambio pero el precio extraido es null, significa que se va a nullificar
        if (hasChange && (extractedPrice === null || extractedPrice === undefined)) {
            return (
                <div>
                    {currentPrice !== null && currentPrice !== undefined && (
                        <span className="text-muted text-decoration-line-through">
                            {formatMoney(currentPrice)}
                        </span>
                    )}
                    <br />
                    <span className="text-warning">
                        - <small>(se eliminará)</small>
                    </span>
                </div>
            )
        }

        if (hasChange && extractedPrice !== null && extractedPrice !== undefined) {
            return (
                <div>
                    {currentPrice !== null && currentPrice !== undefined && (
                        <span className="text-muted text-decoration-line-through">
                            {formatMoney(currentPrice)}
                        </span>
                    )}
                    {currentPrice !== null && currentPrice !== undefined && <br />}
                    <span className={getDifferenceClass(currentPrice, extractedPrice)}>
                        {formatMoney(extractedPrice)}
                        {currentPrice !== null && currentPrice !== undefined && (
                            <small className="ms-1">
                                {getDifferencePercent(currentPrice, extractedPrice)}
                            </small>
                        )}
                    </span>
                </div>
            )
        }

        // Sin cambio, mostrar precio actual o guion si no hay ninguno
        const priceToShow = currentPrice ?? extractedPrice
        return <span className="text-muted">{formatMoney(priceToShow) || '-'}</span>
    }

    // Renderizar celda de descuento (Chile)
    const renderDiscountCell = (
        hasChange: boolean,
        currentDiscount: string | undefined | null,
        extractedDiscount: number | undefined | null,
        suggestedAction?: SuggestedAction
    ) => {
        // Extraer valor numérico del descuento actual (ej: "15%" -> 15)
        const currentValue = currentDiscount
            ? parseFloat(String(currentDiscount).replace('%', '')) || null
            : null

        if (suggestedAction === 'deactivate') {
            return (
                <span className="text-danger">
                    <span className="text-decoration-line-through">{currentDiscount || '-'}</span>
                    <small className="ms-1">(eliminar)</small>
                </span>
            )
        }

        if (hasChange && extractedDiscount !== null && extractedDiscount !== undefined) {
            return (
                <div>
                    {currentDiscount && (
                        <span className="text-muted text-decoration-line-through">
                            {currentDiscount}
                        </span>
                    )}
                    {currentDiscount && <br />}
                    <span className={currentValue && extractedDiscount !== currentValue ? 'text-warning fw-bold' : ''}>
                        {extractedDiscount}%
                    </span>
                </div>
            )
        }

        // Sin cambio
        return <span className="text-muted">{currentDiscount || '-'}</span>
    }

    const canAcceptChange = (change: PriceChange): boolean => {
        const isUnmapped = !change.unit_id && !change.garage_id
        // Solo puede aceptar si esta mapeado o si la accion es deactivate (que ya tiene el ID)
        return !isUnmapped || change.suggested_action === 'deactivate'
    }

    // Verifica si el cambio tiene modificaciones reales que aplicar
    const hasRealChanges = (change: PriceChange): boolean => {
        const suggestedAction = change.suggested_action || 'update_price'
        // Activar/desactivar siempre son cambios reales
        if (suggestedAction === 'activate' || suggestedAction === 'deactivate') {
            return true
        }
        // Para update_price o map, revisar si hay cambios de precio (incluyendo descuento para Chile)
        return change.has_list_change || change.has_cash_change || change.has_work_change || change.has_discount_change
    }

    // Helper para detectar tipo de discrepancia
    const getDiscrepancyType = (runs: DiscrepancyRun[]): DiscrepancyType => {
        const validRuns = runs.filter(r => r !== null)
        if (validRuns.length < 3) {
            return 'partial_presence'
        }
        return 'price_difference'
    }

    // Helper para detectar si hay mayoria (2/3 coinciden)
    const hasMajority = (runs: DiscrepancyRun[]): boolean => {
        const validRuns = runs.filter(r => r !== null)
        if (validRuns.length < 2) return false

        const runStrings = runs.map(r => r ? JSON.stringify(r) : null)
        const counts: Record<string, number> = {}

        runStrings.forEach(str => {
            if (str) {
                counts[str] = (counts[str] || 0) + 1
            }
        })

        return Object.values(counts).some(count => count >= 2)
    }

    // Helper para obtener el indice del run con mayoria
    const getMajorityRunIndex = (runs: DiscrepancyRun[]): number | null => {
        const runStrings = runs.map(r => r ? JSON.stringify(r) : null)
        const counts: Record<string, { count: number; index: number }> = {}

        runStrings.forEach((str, idx) => {
            if (str) {
                if (!counts[str]) {
                    counts[str] = { count: 0, index: idx }
                }
                counts[str].count++
            }
        })

        for (const key in counts) {
            if (counts[key].count >= 2) {
                return counts[key].index
            }
        }

        return null
    }

    // Abrir modal de discrepancia
    const openDiscrepancyModal = (change: PriceChange) => {
        setDiscrepancyChange(change)
        setSelectedRun(null)
        setUseMajority(false)
        setShowDiscrepancyModal(true)
    }

    // Manejar resolucion de discrepancia
    const handleResolveDiscrepancy = async (action: 'use_run' | 'use_majority' | 'reject') => {
        if (!discrepancyChange) return

        if (action === 'use_run' && selectedRun === null) {
            showAlertMessage('warning', 'Seleccione un run primero')
            return
        }

        setSubmitting(true)
        const result = await resolveDiscrepancy({
            change_id: discrepancyChange.id,
            action,
            selected_run: action === 'use_run' ? selectedRun! : undefined
        })
        setSubmitting(false)

        if (result.success) {
            showAlertMessage('success', result.message || 'Discrepancia resuelta')
            setShowDiscrepancyModal(false)
            loadData(statusFilter, projectFilter, showOnlyChanges)
        } else {
            showAlertMessage('danger', result.message)
        }
    }

    // Vista de error si la extracción falló
    if (extraction?.status === 'failed') {
        return (
            <div className="p-4">
                {/* Header */}
                <div className="mb-4 py-2 d-flex justify-content-between flex-wrap align-items-center">
                    <Link to="/admin/price-extractions" className="fs-4">
                        <i className="text-light bi bi-arrow-left me-2"></i>
                        Volver
                    </Link>
                    <h4 className="mb-0">Validacion de Extraccion #{id}</h4>
                    <div style={{ width: 100 }}></div>
                </div>

                <Alert variant="danger" className="text-center">
                    <Alert.Heading>
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        Error en la extracción
                    </Alert.Heading>
                    <p className="mb-0">
                        Hubo un error al procesar el archivo. Por favor, intenta crear una nueva extracción.
                    </p>
                </Alert>
            </div>
        )
    }

    // Vista de procesamiento con polling
    if (isProcessing || (loading && !extraction)) {
        return (
            <div className="p-4">
                {/* Header */}
                <div className="mb-4 py-2 d-flex justify-content-between flex-wrap align-items-center">
                    <Link to="/admin/price-extractions" className="fs-4">
                        <i className="text-light bi bi-arrow-left me-2"></i>
                        Volver
                    </Link>
                    <h4 className="mb-0">Validacion de Extraccion #{id}</h4>
                    <div style={{ width: 100 }}></div>
                </div>

                {/* Loader de procesamiento */}
                <Card className="mb-4">
                    <Card.Body className="text-center py-5">
                        <Lottie animationData={loaderIris} style={{ height: 120, margin: '0 auto' }} loop />
                        <h5 className="mt-3 mb-2">
                            {isProcessing ? 'Procesando extracción...' : 'Cargando...'}
                        </h5>
                        {isProcessing && (
                            <>
                                <p className="text-muted mb-3">
                                    Estamos analizando el archivo con IA. Esto puede tomar unos segundos.
                                </p>
                                <div className="d-flex justify-content-center gap-2 align-items-center">
                                    <Spinner animation="grow" size="sm" variant="primary" />
                                    <small className="text-muted">Actualizando automaticamente...</small>
                                </div>
                            </>
                        )}
                    </Card.Body>
                </Card>
            </div>
        )
    }

    return (
        <div className="p-4">
            {/* Header */}
            <div className="mb-4 py-2 d-flex justify-content-between flex-wrap align-items-center">
                <Link to="/admin/price-extractions" className="fs-4">
                    <i className="text-light bi bi-arrow-left me-2"></i>
                    Volver
                </Link>
                <h4 className="mb-0">Validacion de Extraccion #{id}</h4>
                <div style={{ width: 100 }}></div>
            </div>

            {/* Info de extraccion */}
            {extraction && (
                <Card className="mb-4">
                    <Card.Body>
                        <div className="d-flex flex-wrap gap-4">
                            <div>
                                <small className="text-muted">Archivo</small>
                                <p className="mb-0 fw-bold">{extraction.file_name}</p>
                            </div>
                            <div>
                                <small className="text-muted">Pais</small>
                                <p className="mb-0">{extraction.country?.name}</p>
                            </div>
                            <div>
                                <small className="text-muted">Tipo</small>
                                <p className="mb-0">
                                    <Badge bg={extraction.extraction_type === 'pro' ? 'warning' : 'secondary'}>
                                        {extraction.extraction_type.toUpperCase()}
                                    </Badge>
                                </p>
                            </div>
                            <div>
                                <small className="text-muted">Estado</small>
                                <p className="mb-0">
                                    <Badge bg={extraction.status === 'applied' ? 'success' : 'primary'}>
                                        {extractionStatusLabels[extraction.status]}
                                    </Badge>
                                </p>
                            </div>
                            <div>
                                <small className="text-muted">Proyectos</small>
                                <p className="mb-0">{extraction.total_projects}</p>
                            </div>
                            <div>
                                <small className="text-muted">Unidades</small>
                                <p className="mb-0">{totalUnits}</p>
                            </div>
                            <div>
                                <small className="text-muted">Cocheras</small>
                                <p className="mb-0">{totalGarages}</p>
                            </div>
                            <div>
                                <small className="text-muted">Bauleras</small>
                                <p className="mb-0">{totalWarehouses}</p>
                            </div>
                            <div>
                                <small className="text-muted">Cambios Pendientes</small>
                                <p className="mb-0">
                                    <Badge bg="primary">{extraction.changes_pending}</Badge>
                                </p>
                            </div>
                            <div>
                                <small className="text-muted">Aceptados</small>
                                <p className="mb-0">
                                    <Badge bg="success">{extraction.changes_accepted}</Badge>
                                </p>
                            </div>
                            <div>
                                <small className="text-muted">Aplicados</small>
                                <p className="mb-0">
                                    <Badge bg="info">{extraction.changes_applied}</Badge>
                                </p>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Filtros y acciones masivas */}
            <div className="d-flex mb-3 align-items-center flex-wrap gap-2 justify-content-between">
                <div className="d-flex gap-2 flex-wrap align-items-center">
                    <DropdownButton
                        title={statusFilter === 'all' ? 'Todos los estados' : statusLabels[statusFilter as ChangeStatus]}
                        variant="outline-secondary"
                        size="sm"
                    >
                        <Dropdown.Item onClick={() => handleStatusFilter('all')}>Todos</Dropdown.Item>
                        {Object.entries(statusLabels).map(([key, label]) => (
                            <Dropdown.Item key={key} onClick={() => handleStatusFilter(key)}>
                                {label}
                            </Dropdown.Item>
                        ))}
                    </DropdownButton>

                    <DropdownButton
                        title={projectFilter ? uniqueProjects.find(p => p.id === projectFilter)?.title : 'Todos los proyectos'}
                        variant="outline-secondary"
                        size="sm"
                    >
                        <Dropdown.Item onClick={() => handleProjectFilter(undefined)}>Todos</Dropdown.Item>
                        {uniqueProjects.map(p => (
                            <Dropdown.Item key={p.id} onClick={() => handleProjectFilter(p.id)}>
                                {p.title}
                            </Dropdown.Item>
                        ))}
                    </DropdownButton>

                    <Form.Check
                        type="checkbox"
                        label="Solo con cambios"
                        checked={showOnlyChanges}
                        onChange={(e) => handleShowOnlyChanges(e.target.checked)}
                    />
                </div>

                {extraction?.status !== 'applied' && (
                    <div className="d-flex gap-2">
                        {pendingCount > 0 && (
                            <>
                                <Button
                                    variant="success"
                                    size="sm"
                                    onClick={() => handleValidateAll('accept_all')}
                                    disabled={submitting || acceptablePendingCount === 0}
                                    title={acceptablePendingCount === 0 ? 'No hay cambios que se puedan aceptar (mapea las unidades primero)' : ''}
                                >
                                    Aceptar Mapeados ({acceptablePendingCount})
                                </Button>
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleValidateAll('reject_all')}
                                    disabled={submitting}
                                >
                                    Rechazar Todos ({pendingCount})
                                </Button>
                            </>
                        )}
                        {acceptedCount > 0 && (
                            <Button
                                variant="primary"
                                onClick={() => setShowApplyModal(true)}
                                disabled={submitting}
                            >
                                Aplicar Cambios ({acceptedCount})
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {/* Tabla de cambios */}
            {!loading && changes.length > 0 && (
                <Table bordered hover responsive size="sm">
                    <thead>
                        <tr>
                            <th>Proyecto</th>
                            <th>Tipo</th>
                            <th>Numero</th>
                            <th>Accion</th>
                            <th>Precio Lista</th>
                            {isChile ? (
                                <>
                                    <th>Descuento</th>
                                    <th>Precio Final</th>
                                </>
                            ) : (
                                <>
                                    <th>Precio Contado</th>
                                    <th>Precio Obra</th>
                                </>
                            )}
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {changes.map(change => {
                            const isUnmapped = !change.unit_id && !change.garage_id
                            const suggestedAction = change.suggested_action || 'update_price'
                            const canAccept = canAcceptChange(change)

                            return (
                                <tr
                                    key={change.id}
                                    className={
                                        change.is_discrepancy ? 'table-warning' :
                                        suggestedAction === 'deactivate' ? 'table-danger' :
                                        suggestedAction === 'activate' ? 'table-success' :
                                        ''
                                    }
                                >
                                    <td>
                                        <small>{change.project?.title || `ID: ${change.project_id}`}</small>
                                    </td>
                                    <td>
                                        <Badge bg={change.entity_type === 'unit' ? 'primary' : change.garage_type === 'warehouse' ? 'info' : 'secondary'}>
                                            {getEntityLabel(change)}
                                        </Badge>
                                    </td>
                                    <td>
                                        <strong>{change.extracted_number}</strong>
                                        {suggestedAction === 'map' && (
                                            isUnmapped ? (
                                                <Badge bg="warning" className="ms-1">Sin mapear</Badge>
                                            ) : (
                                                <Badge bg="info" className="ms-1" title={`Mapeado a: ${change.unit?.number || change.garage?.name}`}>
                                                    → {change.unit?.number || change.garage?.name}
                                                </Badge>
                                            )
                                        )}
                                        {change.is_discrepancy && (
                                            <Badge
                                                bg="danger"
                                                className="ms-1"
                                                style={{ cursor: 'pointer' }}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    openDiscrepancyModal(change)
                                                }}
                                                title="Click para resolver discrepancia"
                                            >
                                                Discrepancia
                                            </Badge>
                                        )}
                                        {isResolvedDiscrepancy(change) && (
                                            <Badge
                                                bg="info"
                                                className="ms-1"
                                                style={{ cursor: 'pointer' }}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    openDiscrepancyModal(change)
                                                }}
                                                title="Click para editar resolucion"
                                            >
                                                Resuelto
                                            </Badge>
                                        )}
                                    </td>
                                    <td>
                                        <Badge bg={actionColors[suggestedAction]}>
                                            {actionLabels[suggestedAction]}
                                        </Badge>
                                    </td>
                                    <td>
                                        {renderPriceCell(
                                            change.has_list_change,
                                            change.current_list_price,
                                            change.extracted_list_price,
                                            suggestedAction
                                        )}
                                    </td>
                                    {isChile ? (
                                        <>
                                            <td>
                                                {renderDiscountCell(
                                                    change.has_discount_change,
                                                    change.unit?.max_discount,
                                                    change.extracted_discount,
                                                    suggestedAction
                                                )}
                                            </td>
                                            <td>
                                                {renderPriceCell(
                                                    change.has_cash_change,
                                                    change.unit?.final_price,
                                                    change.extracted_cash_price,
                                                    suggestedAction
                                                )}
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td>
                                                {renderPriceCell(
                                                    change.has_cash_change,
                                                    change.current_price,
                                                    change.extracted_cash_price,
                                                    suggestedAction
                                                )}
                                            </td>
                                            <td>
                                                {renderPriceCell(
                                                    change.has_work_change,
                                                    change.current_work_price,
                                                    change.extracted_work_price,
                                                    suggestedAction
                                                )}
                                            </td>
                                        </>
                                    )}
                                    <td>
                                        <Badge bg={statusColors[change.status]}>
                                            {statusLabels[change.status]}
                                        </Badge>
                                    </td>
                                    <td>
                                        {change.status === 'pending' ? (
                                            <div className="d-flex gap-1">
                                                {/* Botón de mapeo siempre visible si suggested_action es 'map' */}
                                                {suggestedAction === 'map' && (
                                                    <Button
                                                        variant={isUnmapped ? 'outline-warning' : 'outline-info'}
                                                        size="sm"
                                                        onClick={() => openMappingModal(change)}
                                                        title={isUnmapped ? 'Mapear manualmente' : 'Cambiar mapeo'}
                                                    >
                                                        <i className={isUnmapped ? 'bi bi-link-45deg' : 'bi bi-pencil'}></i>
                                                    </Button>
                                                )}
                                                {/* Botones de aceptar/rechazar si hay cambios reales O si es mapeo ya mapeado */}
                                                {(hasRealChanges(change) || (suggestedAction === 'map' && !isUnmapped)) ? (
                                                    <>
                                                        <Button
                                                            variant="outline-success"
                                                            size="sm"
                                                            onClick={() => handleValidateSingle(change.id, 'accepted')}
                                                            disabled={submitting || !canAccept}
                                                            title={canAccept ? 'Aceptar' : 'Debe mapear primero'}
                                                        >
                                                            <i className="bi bi-check"></i>
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleValidateSingle(change.id, 'rejected')}
                                                            disabled={submitting}
                                                            title="Rechazar"
                                                        >
                                                            <i className="bi bi-x"></i>
                                                        </Button>
                                                    </>
                                                ) : suggestedAction !== 'map' ? (
                                                    <small className="text-muted">Sin cambios</small>
                                                ) : null}
                                            </div>
                                        ) : null}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </Table>
            )}

            {loading && (
                <div style={{ height: 200 }} className="w-100 d-flex align-items-center justify-content-center">
                    <Spinner animation="border" />
                </div>
            )}

            {!loading && changes.length === 0 && (
                <Alert variant="info">
                    No hay cambios que mostrar con los filtros seleccionados.
                </Alert>
            )}

            {/* Modal de confirmacion de aplicar */}
            <Modal show={showApplyModal} onHide={() => setShowApplyModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar Aplicacion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Esta a punto de aplicar <strong>{acceptedCount}</strong> cambios a la base de datos.</p>
                    <p className="text-danger">Esta accion actualizara los precios, activara o desactivara unidades segun corresponda. Esta seguro?</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowApplyModal(false)}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleApplyChanges}>
                        Si, Aplicar Cambios
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de mapeo manual */}
            <Modal show={showMappingModal} onHide={() => setShowMappingModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {mappingChange?.unit_id || mappingChange?.garage_id ? 'Cambiar mapeo de ' : 'Mapear '}
                        {mappingChange && getEntityLabel(mappingChange)}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        La {mappingChange && getEntityLabel(mappingChange, false)} extraida <strong>"{mappingChange?.extracted_number}"</strong>
                        {mappingChange?.unit_id || mappingChange?.garage_id ? (
                            <> esta mapeada a <Badge bg="info">{mappingChange?.unit?.number || mappingChange?.garage?.name}</Badge></>
                        ) : (
                            <> no fue encontrada automaticamente.</>
                        )}
                    </p>
                    <p>Seleccione la {mappingChange && getEntityLabel(mappingChange, false)} correcta del proyecto:</p>

                    <SpecialSelect
                        options={getMappingOptions()}
                        onChange={(opt: OptionSelectType) => setSelectedMapping(opt)}
                        selected={selectedMapping}
                        placeholder="Buscar..."
                        noOptionsMessage="No hay opciones disponibles"
                    />

                    {mappingChange && (mappingChange.extracted_list_price || mappingChange.extracted_cash_price || mappingChange.extracted_work_price || mappingChange.extracted_discount) && (
                        <Alert variant="info" className="mt-3 mb-0">
                            <small>
                                <strong>Precios a aplicar:</strong><br />
                                {mappingChange.extracted_list_price && <>Lista: {formatMoney(mappingChange.extracted_list_price)}<br /></>}
                                {isChile ? (
                                    <>
                                        {mappingChange.extracted_discount !== undefined && mappingChange.extracted_discount !== null && <>Descuento: {mappingChange.extracted_discount}%<br /></>}
                                        {mappingChange.extracted_cash_price && <>Precio Final: {formatMoney(mappingChange.extracted_cash_price)}</>}
                                    </>
                                ) : (
                                    <>
                                        {mappingChange.extracted_cash_price && <>Contado: {formatMoney(mappingChange.extracted_cash_price)}<br /></>}
                                        {mappingChange.extracted_work_price && <>Obra: {formatMoney(mappingChange.extracted_work_price)}</>}
                                    </>
                                )}
                            </small>
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowMappingModal(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleMapUnit}
                        disabled={!selectedMapping?.value || submitting}
                    >
                        {submitting ? <Spinner size="sm" animation="border" /> : 'Mapear'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de resolucion de discrepancia */}
            <Modal show={showDiscrepancyModal} onHide={() => setShowDiscrepancyModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {discrepancyChange && isResolvedDiscrepancy(discrepancyChange) ? 'Editar' : 'Resolver'} Discrepancia - {discrepancyChange && getEntityLabel(discrepancyChange)} "{discrepancyChange?.extracted_number}"
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {discrepancyChange?.discrepancy_runs && (() => {
                        const runs = discrepancyChange.discrepancy_runs as DiscrepancyRun[]
                        const discType = getDiscrepancyType(runs)
                        const validRunsCount = runs.filter(r => r !== null).length
                        const canUseMajority = hasMajority(runs)
                        const wasResolved = isResolvedDiscrepancy(discrepancyChange)

                        return (
                            <>
                                {/* Indicador si ya fue resuelta */}
                                {wasResolved && (
                                    <Alert variant="success" className="mb-3">
                                        <small>
                                            <i className="bi bi-check-circle me-1"></i>
                                            Esta discrepancia ya fue resuelta. Puede cambiar la seleccion si lo necesita.
                                        </small>
                                    </Alert>
                                )}

                                {/* Indicador de tipo de discrepancia */}
                                <Alert variant={discType === 'partial_presence' ? 'warning' : 'info'}>
                                    {discType === 'partial_presence' ? (
                                        <>
                                            <strong>Presencia parcial:</strong> Esta unidad fue encontrada en {validRunsCount} de 3 extracciones.
                                            <br />
                                            <small>Decida si la unidad existe realmente o si fue un falso positivo.</small>
                                        </>
                                    ) : (
                                        <>
                                            <strong>Diferencia de precios:</strong> Los 3 runs encontraron la unidad pero con valores diferentes.
                                            <br />
                                            <small>Seleccione cual de los valores es el correcto.</small>
                                        </>
                                    )}
                                </Alert>

                                {/* Tabla comparativa */}
                                <Table bordered size="sm" className="mb-3">
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th className="text-center">
                                                <Form.Check
                                                    type="radio"
                                                    name="runSelection"
                                                    label="Run 1"
                                                    checked={selectedRun === 0}
                                                    onChange={() => { setSelectedRun(0); setUseMajority(false) }}
                                                    disabled={runs[0] === null}
                                                />
                                            </th>
                                            <th className="text-center">
                                                <Form.Check
                                                    type="radio"
                                                    name="runSelection"
                                                    label="Run 2"
                                                    checked={selectedRun === 1}
                                                    onChange={() => { setSelectedRun(1); setUseMajority(false) }}
                                                    disabled={runs[1] === null}
                                                />
                                            </th>
                                            <th className="text-center">
                                                <Form.Check
                                                    type="radio"
                                                    name="runSelection"
                                                    label="Run 3"
                                                    checked={selectedRun === 2}
                                                    onChange={() => { setSelectedRun(2); setUseMajority(false) }}
                                                    disabled={runs[2] === null}
                                                />
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td><strong>Precio Lista</strong></td>
                                            {runs.map((run, idx) => {
                                                const value = run?.precio_lista
                                                const otherValues = runs
                                                    .filter((_, i) => i !== idx)
                                                    .map(r => r?.precio_lista)
                                                    .filter(v => v !== null && v !== undefined)
                                                const isDifferent = value !== null && value !== undefined && otherValues.length > 0 &&
                                                    otherValues.some(v => v !== value)

                                                return (
                                                    <td
                                                        key={idx}
                                                        className={`text-center ${run === null ? 'table-secondary' : isDifferent ? 'table-warning' : ''}`}
                                                    >
                                                        {run === null ? (
                                                            <span className="text-muted">No encontrado</span>
                                                        ) : (
                                                            formatMoney(value) || '-'
                                                        )}
                                                    </td>
                                                )
                                            })}
                                        </tr>
                                        {isChile ? (
                                            <>
                                                <tr>
                                                    <td><strong>Descuento</strong></td>
                                                    {runs.map((run, idx) => {
                                                        const value = run?.descuento
                                                        const otherValues = runs
                                                            .filter((_, i) => i !== idx)
                                                            .map(r => r?.descuento)
                                                            .filter(v => v !== null && v !== undefined)
                                                        const isDifferent = value !== null && value !== undefined && otherValues.length > 0 &&
                                                            otherValues.some(v => v !== value)

                                                        return (
                                                            <td
                                                                key={idx}
                                                                className={`text-center ${run === null ? 'table-secondary' : isDifferent ? 'table-warning' : ''}`}
                                                            >
                                                                {run === null ? (
                                                                    <span className="text-muted">No encontrado</span>
                                                                ) : (
                                                                    value !== null && value !== undefined ? `${value}%` : '-'
                                                                )}
                                                            </td>
                                                        )
                                                    })}
                                                </tr>
                                                <tr>
                                                    <td><strong>Precio Final</strong></td>
                                                    {runs.map((run, idx) => {
                                                        const value = run?.precio_final
                                                        const otherValues = runs
                                                            .filter((_, i) => i !== idx)
                                                            .map(r => r?.precio_final)
                                                            .filter(v => v !== null && v !== undefined)
                                                        const isDifferent = value !== null && value !== undefined && otherValues.length > 0 &&
                                                            otherValues.some(v => v !== value)

                                                        return (
                                                            <td
                                                                key={idx}
                                                                className={`text-center ${run === null ? 'table-secondary' : isDifferent ? 'table-warning' : ''}`}
                                                            >
                                                                {run === null ? (
                                                                    <span className="text-muted">No encontrado</span>
                                                                ) : (
                                                                    formatMoney(value) || '-'
                                                                )}
                                                            </td>
                                                        )
                                                    })}
                                                </tr>
                                            </>
                                        ) : (
                                            <>
                                                <tr>
                                                    <td><strong>Precio Contado</strong></td>
                                                    {runs.map((run, idx) => {
                                                        const value = run?.precio_contado
                                                        const otherValues = runs
                                                            .filter((_, i) => i !== idx)
                                                            .map(r => r?.precio_contado)
                                                            .filter(v => v !== null && v !== undefined)
                                                        const isDifferent = value !== null && value !== undefined && otherValues.length > 0 &&
                                                            otherValues.some(v => v !== value)

                                                        return (
                                                            <td
                                                                key={idx}
                                                                className={`text-center ${run === null ? 'table-secondary' : isDifferent ? 'table-warning' : ''}`}
                                                            >
                                                                {run === null ? (
                                                                    <span className="text-muted">No encontrado</span>
                                                                ) : (
                                                                    formatMoney(value) || '-'
                                                                )}
                                                            </td>
                                                        )
                                                    })}
                                                </tr>
                                                <tr>
                                                    <td><strong>Precio Obra</strong></td>
                                                    {runs.map((run, idx) => {
                                                        const value = run?.plazo_obra
                                                        const otherValues = runs
                                                            .filter((_, i) => i !== idx)
                                                            .map(r => r?.plazo_obra)
                                                            .filter(v => v !== null && v !== undefined)
                                                        const isDifferent = value !== null && value !== undefined && otherValues.length > 0 &&
                                                            otherValues.some(v => v !== value)

                                                        return (
                                                            <td
                                                                key={idx}
                                                                className={`text-center ${run === null ? 'table-secondary' : isDifferent ? 'table-warning' : ''}`}
                                                            >
                                                                {run === null ? (
                                                                    <span className="text-muted">No encontrado</span>
                                                                ) : (
                                                                    formatMoney(value) || '-'
                                                                )}
                                                            </td>
                                                        )
                                                    })}
                                                </tr>
                                            </>
                                        )}
                                    </tbody>
                                </Table>

                                {/* Opcion de usar mayoria */}
                                {canUseMajority && discType === 'price_difference' && (
                                    <Form.Check
                                        type="checkbox"
                                        label={`Usar valor mas frecuente (mayoria 2/3) - Run ${(getMajorityRunIndex(runs) || 0) + 1}`}
                                        checked={useMajority}
                                        onChange={(e) => {
                                            setUseMajority(e.target.checked)
                                            if (e.target.checked) {
                                                setSelectedRun(null)
                                            }
                                        }}
                                        className="mb-3"
                                    />
                                )}

                                {/* Resumen de seleccion */}
                                {(selectedRun !== null || useMajority) && (
                                    <Alert variant="success" className="mb-0">
                                        <strong>Valores a usar:</strong>
                                        {(() => {
                                            const runData = useMajority
                                                ? runs[getMajorityRunIndex(runs) || 0]
                                                : runs[selectedRun!]
                                            if (!runData) return null
                                            return (
                                                <ul className="mb-0 mt-2">
                                                    {runData.precio_lista && <li>Precio Lista: {formatMoney(runData.precio_lista)}</li>}
                                                    {isChile ? (
                                                        <>
                                                            {runData.descuento !== undefined && runData.descuento !== null && <li>Descuento: {runData.descuento}%</li>}
                                                            {runData.precio_final && <li>Precio Final: {formatMoney(runData.precio_final)}</li>}
                                                        </>
                                                    ) : (
                                                        <>
                                                            {runData.precio_contado && <li>Precio Contado: {formatMoney(runData.precio_contado)}</li>}
                                                            {runData.plazo_obra && <li>Precio Obra: {formatMoney(runData.plazo_obra)}</li>}
                                                        </>
                                                    )}
                                                </ul>
                                            )
                                        })()}
                                    </Alert>
                                )}
                            </>
                        )
                    })()}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDiscrepancyModal(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => handleResolveDiscrepancy('reject')}
                        disabled={submitting}
                    >
                        Rechazar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => handleResolveDiscrepancy(useMajority ? 'use_majority' : 'use_run')}
                        disabled={submitting || (!useMajority && selectedRun === null)}
                    >
                        {submitting ? <Spinner size="sm" animation="border" /> : (discrepancyChange && isResolvedDiscrepancy(discrepancyChange) ? 'Guardar' : 'Usar Seleccion')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}
